/**
 * appRecovery.js
 * Handles white-screen / corrupted-storage recovery for QuickCom.
 *
 * Anti-loop strategy:
 *   - sessionStorage._qc_recoveries  tracks how many times we've recovered
 *     (sessionStorage survives JS reloads but is wiped when the tab is closed,
 *      so an infinite loop can't span across sessions)
 *   - 1st attempt  → soft reset  (auth + session keys only)
 *   - 2nd attempt  → hard reset  (full localStorage.clear())
 *   - 3rd+ attempt → stop looping, show persistent-error message
 */

// Keys belonging to auth / session data (safe to wipe without losing UX prefs)
const SESSION_KEYS = [
    'token', 'user', 'selectedAccount', 'accounts', '_email', '_id',
    'lastRoute', 'routeHistory',
];

// Keys belonging to persisted UI state
const UI_STATE_KEYS = [
    'tab_activity_main', 'tab_activity_table1', 'tab_activity_table2',
    'tab_catalogue', 'tab_settings', 'tab_stock', 'tab_technical_files',
    'activity_dateRange', 'activity_periodLabel',
];

/** Remove only auth/session + UI-state keys, keep darkMode etc. */
export const softReset = () => {
    [...SESSION_KEYS, ...UI_STATE_KEYS].forEach(k => localStorage.removeItem(k));
    // Dynamic product-filter keys
    Object.keys(localStorage).forEach(k => {
        if (
            k.startsWith('productFilters_') ||
            k.startsWith('productPage_') ||
            k.startsWith('productItemsPerPage_')
        ) {
            localStorage.removeItem(k);
        }
    });
    console.warn('[QC Recovery] Soft reset: session & UI-state keys cleared.');
};

/** Wipe everything */
export const hardReset = () => {
    localStorage.clear();
    console.warn('[QC Recovery] Hard reset: localStorage cleared entirely.');
};

// ─── Recovery counter ────────────────────────────────────────────────────────

export const getRecoveryCount = () =>
    parseInt(sessionStorage.getItem('_qc_recoveries') || '0', 10);

const incrementRecoveryCount = () => {
    const next = getRecoveryCount() + 1;
    sessionStorage.setItem('_qc_recoveries', String(next));
    return next;
};

export const resetRecoveryCount = () =>
    sessionStorage.removeItem('_qc_recoveries');

// ─── Lifecycle markers ───────────────────────────────────────────────────────

/** Call this as soon as the React tree mounts successfully. */
export const markAppLoaded = () => {
    sessionStorage.setItem('_qc_loaded', 'true');
    sessionStorage.removeItem('_qc_boot');
    resetRecoveryCount(); // successful load → reset counter
};

/** Returns true if the app previously loaded OK in this browser session. */
export const wasAppLoaded = () =>
    sessionStorage.getItem('_qc_loaded') === 'true';

// ─── Main recovery entry point ───────────────────────────────────────────────

/**
 * Performs the appropriate reset and reloads the page.
 * Safe to call from both the ErrorBoundary and the fallback button.
 */
export const performRecovery = () => {
    const attempt = incrementRecoveryCount();
    console.warn(`[QC Recovery] Attempt #${attempt}`);

    if (attempt >= 3) {
        // Stop looping – just reload without clearing anything else
        console.warn('[QC Recovery] Max attempts reached, reloading only.');
    } else if (attempt >= 2) {
        hardReset();
    } else {
        softReset();
    }

    sessionStorage.removeItem('_qc_boot');
    window.location.reload();
};

/**
 * Reload without touching storage (first-resort gentle retry).
 * Uses a separate counter so it doesn't count against recovery attempts.
 */
export const gentleReload = () => {
    const reloads = parseInt(sessionStorage.getItem('_qc_gentle_reloads') || '0', 10);
    if (reloads >= 1) {
        // Already tried a gentle reload and it didn't help → do a real recovery
        sessionStorage.removeItem('_qc_gentle_reloads');
        performRecovery();
        return;
    }
    sessionStorage.setItem('_qc_gentle_reloads', String(reloads + 1));
    window.location.reload();
};

